import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { Eye, EyeOff, Check, X, Loader2, User, Lock, Mail, UserX } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useModal } from '../context/ModalContext';

// Helper component for each individual child card
const ChildNameCard = ({ child, onRefreshUser }) => {
  const modal = useModal();
  const [childName, setChildName] = useState(child.name || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Keep input in sync if child object changes
  useEffect(() => {
    setChildName(child.name || '');
  }, [child]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedName = childName.trim();
    if (!trimmedName || trimmedName.length < 2 || trimmedName.length > 50) {
      setError('Name must be between 2 and 50 characters.');
      return;
    }

    setLoading(true);
    try {
      await authService.updateChildName({ childId: child.id, name: trimmedName });
      await onRefreshUser();
      setSuccess(`${child.name}'s name has been updated to ${trimmedName}!`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update child name.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChild = async () => {
    const confirmMsg = `Are you sure you want to permanently delete ${child.name} and their wishlist?`;
    const confirmed = await modal.confirm({
      title: 'Delete Child Account',
      message: confirmMsg,
      variant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    setError('');
    setSuccess('');
    setDeleting(true);

    try {
      await authService.deleteChildAccount(child.id);
      await onRefreshUser();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to delete child account.');
      setDeleting(false);
    }
  };

  const inputId = `child-name-input-${child.id}`;

  return (
    <div className="card h-fit">
      <h3 className="text-lg font-bold text-xmas-green flex items-center gap-2 mb-4">
        <User size={20} /> {child.name}'s Displayed Name
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">First Name</label>
          <input
            id={inputId}
            type="text"
            className="w-full p-2 border rounded mt-1 focus:ring-xmas-green focus:border-xmas-green"
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            required
            minLength={2}
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">This is how your child is displayed in family groups.</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded text-sm">
            {success}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary w-full disabled:opacity-50"
          disabled={loading || deleting}
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Saving Name...
            </>
          ) : (
            'Update Name'
          )}
        </button>

        <button
          type="button"
          onClick={handleDeleteChild}
          disabled={loading || deleting}
          className="btn-secondary w-full text-sm disabled:opacity-50"
        >
          {deleting ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Deleting Child Account...
            </>
          ) : (
            <>
              <UserX size={18} />
              Delete Child Account
            </>
          )}
        </button>
      </form>
    </div>
  );
};

const Account = () => {
  useSEO({
    title: 'Account Settings',
    description: 'Manage your Family Wishlist account settings including displayed name and password updates.',
    keywords: 'family wishlist account, settings, change password, update name'
  });

  const { user, refreshUser } = useAuth();

  // Name form state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameSuccess, setNameSuccess] = useState('');
  const [nameLoading, setNameLoading] = useState(false);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);

  // Initialize name when user loads
  useEffect(() => {
    if (user && user.name) {
      setName(user.name);
    }
  }, [user]);

  // Sync password match error
  useEffect(() => {
    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      setPasswordMatchError(true);
    } else {
      setPasswordMatchError(false);
    }
  }, [newPassword, confirmNewPassword]);

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'At least one uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'At least one lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'At least one number', test: (p) => /\d/.test(p) },
    { label: 'At least one special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
  ];

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    setNameError('');
    setNameSuccess('');

    const trimmedName = name.trim();
    if (!trimmedName || trimmedName.length < 2 || trimmedName.length > 50) {
      setNameError('Name must be between 2 and 50 characters.');
      return;
    }

    setNameLoading(true);
    try {
      await authService.updateName({ name: trimmedName });
      await refreshUser();
      setNameSuccess('Your displayed name has been updated successfully!');
      setTimeout(() => setNameSuccess(''), 5000);
    } catch (err) {
      setNameError(err.response?.data?.msg || 'Failed to update name.');
    } finally {
      setNameLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Check password rules
    const allMet = passwordRequirements.every(req => req.test(newPassword));
    if (!allMet) {
      setPasswordError('Please ensure your new password meets all requirements.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      await authService.updatePassword({
        currentPassword,
        newPassword,
        confirmNewPassword
      });
      setPasswordSuccess('Your password has been successfully updated.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (err) {
      setPasswordError(err.response?.data?.msg || 'Failed to update password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center mt-20">
        <Loader2 className="animate-spin inline-block text-xmas-red" size={40} />
        <p className="mt-2 text-gray-600">Loading your account details...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-10 px-4">
      <h2 className="text-3xl font-bold text-center text-xmas-red mb-2">Account Settings</h2>
      <p className="text-center text-gray-600 mb-8">Manage your profile details and security settings below.</p>

      {/* Account Info Card (Email, read-only) */}
      <div className="card mb-8">
        <h3 className="text-lg font-bold text-xmas-green flex items-center gap-2 mb-4">
          <Mail size={20} /> Account Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500">Registered Email</label>
            <p className="text-lg font-semibold text-gray-800 mt-1">{user.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Account Status</label>
            <p className="text-lg font-semibold text-green-600 mt-1 flex items-center gap-1">
              <Check size={18} /> Verified Account
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* Left Column: Own Name & Children's Names */}
        <div className="space-y-8 h-fit">
          {/* Name Form Card */}
          <div className="card h-fit">
            <h3 className="text-lg font-bold text-xmas-green flex items-center gap-2 mb-4">
              <User size={20} /> Displayed Name
            </h3>
            <form onSubmit={handleNameSubmit} className="space-y-4">
              <div>
                <label htmlFor="parent-name-input" className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  id="parent-name-input"
                  type="text"
                  className="w-full p-2 border rounded mt-1 focus:ring-xmas-green focus:border-xmas-green"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={50}
                />
                <p className="text-xs text-gray-500 mt-1">This is how your family members see you in groups.</p>
              </div>

              {nameError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
                  {nameError}
                </div>
              )}

              {nameSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded text-sm">
                  {nameSuccess}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full disabled:opacity-50"
                disabled={nameLoading}
              >
                {nameLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Saving Name...
                  </>
                ) : (
                  'Update Name'
                )}
              </button>
            </form>
          </div>

          {/* Children Name Cards */}
          {user.children && user.children.length > 0 && (
            user.children.map((child) => (
              <ChildNameCard
                key={child.id}
                child={child}
                onRefreshUser={refreshUser}
              />
            ))
          )}
        </div>

        {/* Password Form Card */}
        <div className="card">
          <h3 className="text-lg font-bold text-xmas-green flex items-center gap-2 mb-4">
            <Lock size={20} /> Change Password
          </h3>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Password</label>
              <div className="relative mt-1">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  className="w-full p-2 border rounded pr-10 focus:ring-xmas-green focus:border-xmas-green"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 text-gray-500"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <div className="relative mt-1">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="w-full p-2 border rounded pr-10 focus:ring-xmas-green focus:border-xmas-green"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 text-gray-500"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req, index) => {
                  const met = req.test(newPassword);
                  return (
                    <div key={index} className={`flex items-center text-xs ${met ? 'text-green-600' : 'text-gray-500'}`}>
                      {met ? <Check size={12} className="mr-1" /> : <X size={12} className="mr-1" />}
                      {req.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <div className="relative mt-1">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`w-full p-2 border rounded pr-10 focus:ring-xmas-green focus:border-xmas-green ${passwordMatchError ? 'border-red-500' : ''}`}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 text-gray-500"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {passwordMatchError && (
                <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
              )}
            </div>

            {passwordError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded text-sm">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded text-sm">
                {passwordSuccess}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full disabled:opacity-50"
              disabled={passwordLoading}
            >
              {passwordLoading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Updating Password...
                </>
              ) : (
                'Change Password'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Delete account message */}
      <div className="border-t border-gray-200 mt-12 pt-8 text-center pb-6">
        <p className="text-sm text-gray-500">
          If you wish to delete your account, please email{' '}
          <a href="mailto:privacy@family-wishlist.com" className="text-xmas-red font-semibold hover:underline">
            privacy@family-wishlist.com
          </a>.
        </p>
      </div>
    </div>
  );
};

export default Account;
